/**
 * render.js
 * Funciones que construyen y actualizan el DOM.
 * Responsabilidad única: traducir datos a HTML visible.
 * No contiene lógica de negocio ni gestión de eventos.
 */

import { getFiltradas } from './state.js';
import { claseBadgeCategoria } from './filters.js';
import { escaparHTML } from './utils.js';

/**
 * Renderiza la lista de tarjetas de recetas en el DOM.
 * @param {Function} onClickTarjeta - callback al hacer clic en una tarjeta
 * @param {boolean}  admin          - si true, muestra botón de editar
 * @param {Function} onClickEditar  - callback al pulsar editar
 */
export function renderLista(onClickTarjeta, admin = false, onClickEditar = null) {
  const contenedor = document.getElementById('lista-recetas');
  const contador   = document.getElementById('contador');
  const filtradas  = getFiltradas();

  // ── Actualizar contador ───────────────────────────
  const total = filtradas.length;
  contador.textContent = total === 0
    ? ''
    : `${total} ${total === 1 ? 'receta' : 'recetas'}`;

  // ── Estado vacío ──────────────────────────────────
  if (total === 0) {
    contenedor.innerHTML = `
      <div class="estado-vacio" role="status">
        <p class="estado-vacio__texto">No se encontraron recetas</p>
        <button class="estado-vacio__btn" id="btn-limpiar">
          Limpiar búsqueda
        </button>
      </div>
    `;
    return;
  }

  // ── Generar tarjetas ──────────────────────────────
  contenedor.innerHTML = filtradas
    .map((receta) => {
      const claseCategoria = claseBadgeCategoria(receta.categoria);
      const ingredientesCortos = receta.ingredientes
        .split('\n')
        .slice(0, 3)
        .join(' · ');

      const btnEditar = admin
        ? `<button
             class="tarjeta__editar"
             data-edit-id="${receta.id}"
             aria-label="Editar ${escaparHTML(receta.nombre)}"
             title="Editar receta"
           >✏️</button>`
        : '';

      return `
        <article
          class="tarjeta"
          role="listitem"
          data-id="${receta.id}"
          tabindex="0"
          aria-label="${escaparHTML(receta.nombre)}, ${escaparHTML(receta.categoria)}"
        >
          ${btnEditar}
          <span class="badge-categoria ${claseCategoria}">
            ${escaparHTML(receta.categoria)}
          </span>
          <h2 class="tarjeta__nombre">${escaparHTML(receta.nombre)}</h2>
          <p class="tarjeta__ingredientes">${escaparHTML(ingredientesCortos)}</p>
        </article>
      `;
    })
    .join('');

  // ── Eventos de clic y teclado en tarjetas ─────────
  contenedor.querySelectorAll('.tarjeta').forEach((tarjeta) => {
    tarjeta.addEventListener('click', (e) => {
      // Si el clic viene del botón editar, no abrir detalle
      if (e.target.closest('[data-edit-id]')) { return; }
      onClickTarjeta(Number(tarjeta.dataset.id));
    });

    tarjeta.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClickTarjeta(Number(tarjeta.dataset.id));
      }
    });
  });

  // ── Eventos del botón editar ──────────────────────
  if (admin && onClickEditar) {
    contenedor.querySelectorAll('[data-edit-id]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id     = Number(btn.dataset.editId);
        const receta = getFiltradas().find((r) => r.id === id);
        if (receta) { onClickEditar(receta); }
      });
    });
  }
}

/**
 * Renderiza el estado de carga inicial.
 */
export function renderCargando() {
  const contenedor = document.getElementById('lista-recetas');
  contenedor.innerHTML = `
    <div class="estado-vacio" role="status" aria-live="polite">
      <p class="estado-vacio__texto">Cargando recetas...</p>
    </div>
  `;
}

/**
 * Renderiza un mensaje de error.
 * @param {string} mensaje
 */
export function renderError(mensaje) {
  const contenedor = document.getElementById('lista-recetas');
  contenedor.innerHTML = `
    <div class="estado-vacio" role="alert">
      <p class="estado-vacio__texto">No se pudieron cargar las recetas</p>
      <p style="color: var(--color-text-soft); font-size: var(--text-sm)">
        ${mensaje}
      </p>
    </div>
  `;
}